import { FlexBox, Skeleton } from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { PercentageBar } from 'lib-analytics-ui';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { memo, useMemo } from 'react';

import {
  DashboardLegend,
  DashboardLegendItem,
} from 'app/views/layout/views/dashboard/components/legend';

import { useChannelDashboardChannelContext } from '../../../../channelDashboardChannel.context';
import { ChannelDashboardMetric } from '../../../metric/channelDashboardMetric';

type ChannelDashboardAgentPresenceMetricProps = PropsWithClassName;

export const ChannelDashboardAgentPresenceMetric = memo(
  function QueueDashboardAgentPresenceMetric({
    className,
  }: ChannelDashboardAgentPresenceMetricProps): JSX.Element {
    const { metrics, isSkeleton } = useChannelDashboardChannelContext();

    const availableTotal = metrics?.agents_available ?? 0;
    const onlineTotal = metrics?.agents_online ?? 0;

    // The number of available agents cannot be higher than the number of online agents. At best,
    // the numbers can be equal, but the count of available agents is a subset of those online.
    const percentage =
      onlineTotal && availableTotal <= onlineTotal
        ? Math.round((availableTotal / onlineTotal) * 100)
        : 0;

    const legendItems: DashboardLegendItem[] = useMemo(
      () => [
        {
          type: 'number',
          title: 'Available',
          format: 'number',
          value: availableTotal,
          color: colors.purple['700'],
        },
        {
          type: 'number',
          title: 'Online',
          format: 'number',
          value: onlineTotal,
          color: colors.neutral[200],
        },
      ],
      [availableTotal, onlineTotal],
    );

    return (
      <ChannelDashboardMetric
        className={className}
        title="Agents presence"
        isSkeleton={isSkeleton}
        metricType="agent_presence"
      >
        <FlexBox flexDirection="column" gap="200" maxWidth="224px">
          {isSkeleton ? (
            <Skeleton height="20px" width="auto" />
          ) : (
            <PercentageBar
              value={percentage}
              showFullBar
              theme={
                [legendItems[0].color, legendItems[1].color] as [string, string]
              }
            />
          )}

          <DashboardLegend items={legendItems} isSkeleton={isSkeleton} />
        </FlexBox>
      </ChannelDashboardMetric>
    );
  },
);
